import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";
import { InvoiceSendDialog } from "@/components/pawkt/InvoiceSendDialog";

export const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { key: "email", label: "Email", icon: Mail },
];

// Shared invoice delivery logic (PDF download + WhatsApp/Email/SMS send with confirm dialog).
// Used by the inline InvoiceActions bar and the Invoices page row menu.
export function useInvoiceDelivery(invoice) {
  const [confirm, setConfirm] = useState(null); // channel key
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    if (pdfBusy) return; // prevent double-clicks / duplicate requests
    setPdfBusy(true);
    try {
      const r = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const blob = r.data;
      // Guard against a non-PDF payload being treated as a valid file.
      if (!blob || (blob.type && blob.type.indexOf("application/pdf") === -1)) {
        throw new Error("Unexpected response");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number || invoice.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // Log real context so genuine production failures are diagnosable from the console.
      console.error("invoice pdf download failed", { id: invoice.id, status: e?.response?.status, err: e?.message });
      // Surface the real server reason when available (error bodies arrive as a Blob
      // because responseType is "blob") so genuine failures are diagnosable.
      let msg = "Unable to generate the invoice PDF. Please try again.";
      try {
        const body = e?.response?.data;
        if (body instanceof Blob) {
          const text = await body.text();
          const parsed = JSON.parse(text);
          const d = parsed?.detail;
          msg = (typeof d === "object" ? d?.message : d) || msg;
        }
      } catch { /* keep generic message */ }
      toast.error(msg);
    } finally { setPdfBusy(false); }
  };

  const dialog = confirm && <InvoiceSendDialog invoice={invoice} channel={confirm} onClose={() => setConfirm(null)} />;

  return { downloadPdf, pdfBusy, openSend: setConfirm, dialog };
}

// Reusable invoice delivery bar: Download PDF + one-click WhatsApp / Email / SMS.
// Honest provider states (demo / not-configured / WhatsApp-template-required).
export function InvoiceActions({ invoice, size = "sm", className = "" }) {
  const { downloadPdf, pdfBusy, openSend, dialog } = useInvoiceDelivery(invoice);
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={`invoice-actions-${invoice.id}`}>
      <Button variant="outline" size={size} className="gap-1.5 h-8 text-xs" data-testid={`invoice-download-${invoice.id}`}
        disabled={pdfBusy} onClick={downloadPdf}><Download className="h-3.5 w-3.5" /> {pdfBusy ? "Generating…" : "PDF"}</Button>
      {CHANNELS.map(({ key, label, icon: Icon }) => (
        <Button key={key} variant="outline" size={size} className="gap-1.5 h-8 text-xs"
          data-testid={`invoice-send-${key}-${invoice.id}`} onClick={() => openSend(key)}>
          <Icon className="h-3.5 w-3.5" /> {label}
        </Button>
      ))}
      {dialog}
    </div>
  );
}
